<template>
  <div
    ref="container"
    v-intersection="intersectionConfig"
    class="outreach-contact-list__items"
  >
    <OutreachContactListItem
      v-for="(item, index) in itemsFiltered"
      :key="index"
      ref="itemComponents"
      :clickable="clickable"
      :data-cy="`outreachContactListItem${index}`"
      :force-tooltip="forceTooltip"
      :max-width="`${maxItemWidth}px`"
      :show-comma="!isLastItem(index, itemsFiltered.length)"
      @click="onClick(item)"
    >
      {{ item[localDisplayKey] }}

      <template #tooltip="{ isEllipsed }">
        <slot
          :is-ellipsed="isEllipsed"
          :item="item"
          name="itemTooltip"
        >
          {{ item[localDisplayKey] }}
        </slot>
      </template>
    </OutreachContactListItem>

    <BaseButtonDropdown
      v-if="itemsRemaining > 0"
      interaction="hover"
      placement="bottom"
      submenu
    >
      <template #btn="{ listeners }">
        <div
          ref="seeAllContainer"
          class="outreach-contact-list__popover-activator"
          v-on="listeners"
        >
          <a class="outreach-contact-list__item-link">
            {{ seeAllText }}
          </a>
        </div>
      </template>

      <template #default>
        <QCard class="outreach-contact-list__popover">
          <QCardSection v-if="title">
            <div class="outreach-contact-list__popover-title">
              {{ title }}
            </div>
          </QCardSection>
          <QCardSection :class="{ 'q-pt-none': Boolean(title) }">
            <div class="outreach-contact-list__popover-items">
              <OutreachContactListItem
                v-for="(item, index) in items"
                :key="index"
                :force-tooltip="forceTooltip"
                :show-comma="!isLastItem(index, items.length)"
                @click="onClick(item)"
              >
                {{ item[localDisplayKey] }}

                <template #tooltip>
                  <slot
                    :item="item"
                    name="itemPopoverTooltip"
                  >
                    {{ item[localDisplayKey] }}
                  </slot>
                </template>
              </OutreachContactListItem>
            </div>
          </QCardSection>
        </QCard>
      </template>
    </BaseButtonDropdown>
  </div>
</template>

<script lang="ts">
interface OutreachContactListItemsPropItem {
  name: string;
}
</script>

<script setup lang="ts" generic="T extends OutreachContactListItemsPropItem">
import { type IntersectionValue } from "quasar";
import {
  computed,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  useTemplateRef,
} from "vue";

import { getLocaleText } from "shared/boot/i18n";
import { BaseButtonDropdown } from "shared/components/base";
import OutreachContactListItem from "shared/components/Outreach/OutreachContact/OutreachContactListItem.vue";

const MAX_ITEM_WIDTH = 150;
const GAP = 2;

interface OutreachContactListItemsProps {
  clickable?: boolean;
  displayKey?: keyof T;
  forceTooltip?: boolean;
  items: T[];
  showAll?: boolean;
  title?: string;
}

const props = withDefaults(defineProps<OutreachContactListItemsProps>(), {
  displayKey: "name" as keyof T,
  title: "",
});

const emit = defineEmits(["click"]);

const container = ref();
const seeAllContainer = ref();

const localDisplayKey = computed<keyof T>(() => props.displayKey || "name");

const itemComponents =
  useTemplateRef<(typeof OutreachContactListItem)[]>("itemComponents");

const maxItemWidth = ref(MAX_ITEM_WIDTH);
const maxItems = ref(-1);

const totalItems = computed(() => props.items.length);

const init = ref(false);
const visible = ref(false);
const calculating = ref(false);

const itemsFiltered = computed<T[]>(() => {
  if (maxItems.value === -1 || calculating.value || props.showAll) {
    return props.items;
  }

  return props.items.slice(0, maxItems.value);
});

const itemsRemaining = computed(() => {
  if (maxItems.value === -1 || props.showAll) {
    return 0;
  }

  if (maxItems.value > totalItems.value) {
    return 0;
  }

  return totalItems.value - maxItems.value;
});

const seeAllText = computed(() => {
  const isLastItemEllipsed = itemComponents.value
    ? itemComponents.value[itemComponents.value.length - 1]?.isEllipsed()
    : false;

  const prefix = isLastItemEllipsed ? "" : "...";
  const description = getLocaleText("outreach_contact_list_items.see_all");
  const count = `(${totalItems.value})`;

  return `${prefix}${description} ${count}`;
});

function onClick(item: T) {
  if (!props.clickable) {
    return;
  }

  emit("click", item);
}

function isLastItem(index: number, length: number) {
  return index === length - 1;
}

function calculateMaxItems() {
  const containerWidth = container.value?.getBoundingClientRect()?.width || 0;

  const seeAllWidth =
    seeAllContainer.value?.getBoundingClientRect()?.width || 0;

  const availableWidth = containerWidth - seeAllWidth - GAP;

  maxItemWidth.value = Math.min(MAX_ITEM_WIDTH, availableWidth);

  let totalWidth = 0;
  let itemCount = 0;

  if (!itemComponents.value) return 0;

  itemComponents.value.some((item) => {
    const itemWidth = item.clientWidth() + GAP;

    if (totalWidth + itemWidth > availableWidth) {
      return true;
    }

    totalWidth += itemWidth;
    itemCount += 1;

    return false;
  });

  if (itemCount === 0) {
    maxItemWidth.value = availableWidth;
    itemCount = 1;
  }

  if (totalWidth && totalWidth < availableWidth) {
    const extraWidthPerItem = Math.floor(
      (availableWidth - totalWidth) / itemCount
    );

    maxItemWidth.value += extraWidthPerItem;
  }

  return itemCount;
}

function onRecalculateMaxItems() {
  if (!visible.value) {
    return;
  }

  calculating.value = true;

  nextTick(() => {
    maxItems.value = calculateMaxItems();

    nextTick(() => {
      if (itemsRemaining.value > 0) {
        maxItems.value = calculateMaxItems();
      }

      calculating.value = false;
    });
  });
}

const intersectionConfig = computed(() => ({
  handler: onIntersection,
})) as IntersectionValue;

function onIntersection(entry?: IntersectionObserverEntry) {
  if (!entry) return;

  visible.value = entry.isIntersecting;

  if (entry.isIntersecting && !init.value) {
    onRecalculateMaxItems();
    init.value = true;
  }
}

onBeforeMount(() => {
  window.addEventListener("resize", onRecalculateMaxItems);
});

onBeforeUnmount(() => {
  window.removeEventListener("resize", onRecalculateMaxItems);
});
</script>

<style lang="scss" scoped>
.outreach-contact-list {
  &__items {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
    gap: 0.15rem;
  }

  &__popover {
    max-height: 250px;
    overflow-y: auto;

    &-activator {
      margin-left: -2px;

      a {
        cursor: default;
        color: var(--s-color-blue-9);

        &:hover {
          color: var(--s-color-blue-5);
        }
      }
    }

    &-title {
      font-size: var(--s-font-size-md);
      font-weight: var(--s-font-weight-bold);
      line-height: 150%;
      color: var(--s-color-denim-9);
    }

    &-items {
      display: flex;
      flex-wrap: wrap;
      gap: 0.5rem;
      max-width: 350px;
    }
  }
}
</style>
